import { DirectionProvider } from "@radix-ui/react-direction";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { RateLimitError } from "@/lib/api/client";
import { Toaster } from "@/components/ui/toaster";
import { toast } from "@/components/ui/use-toast";
import { TooltipProvider } from "@/components/ui/tooltip";
import i18nInstance from "@/lib/i18n";
import { getPublicTheme } from "@/lib/api/theme";
import { applyPrimaryColor, applySidebarColors } from "@/lib/theme/applyBrandColor";
import { useThemeStore } from "@/lib/theme/themeStore";

const client = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false },
  },
  mutationCache: new MutationCache({
    onError(error: unknown) {
      if (error instanceof RateLimitError) {
        toast({
          description: i18nInstance.t("errors.rateLimitedCountdown", {
            seconds: error.retryAfter,
          }),
          variant: "destructive",
        });
      }
    },
  }),
});

export function AppProviders({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const [dir, setDir] = useState<"ltr" | "rtl">(i18n.language === "ar" ? "rtl" : "ltr");

  useEffect(() => {
    const handler = (lng: string) => setDir(lng === "ar" ? "rtl" : "ltr");
    i18n.on("languageChanged", handler);
    return () => {
      i18n.off("languageChanged", handler);
    };
  }, [i18n]);

  // Restore saved sidebar colors from localStorage immediately (synchronous).
  useEffect(() => {
    const { sidebarBg, sidebarStroke } = useThemeStore.getState();
    applySidebarColors(sidebarBg, sidebarStroke);
  }, []);

  // Apply saved brand colour from the public (unauthenticated) theme endpoint
  // so the palette is correct on every page load, including the login screen.
  useEffect(() => {
    getPublicTheme()
      .then((theme) => {
        if (theme.brand_primary_color) applyPrimaryColor(theme.brand_primary_color);
      })
      .catch(() => {
        // silently ignore — falls back to the default tokens.css palette
      });
  }, []);

  return (
    <DirectionProvider dir={dir}>
      <QueryClientProvider client={client}>
        <TooltipProvider delayDuration={300}>
          {children}
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </DirectionProvider>
  );
}
